import { create } from "zustand";
import {
  getEditorImportRestoreOwner,
  isEditorImportRestoreTransaction,
} from "../services/editor/editorImportRestoreOwner";
import type {
  UnityObjectRenderPhase,
  UnityObjectRenderStatePayload,
} from "../services/editor/objectRenderState";

export interface ObjectRenderOverlayEntry extends UnityObjectRenderStatePayload {
  key: string;
  startedAt: number;
  updatedAt: number;
}

/** EditorCanvas "Rendering object..." overlay와 동일한 가시성 판정. */
export function isEditorObjectRenderingOverlayVisible(
  entry: ObjectRenderOverlayEntry | null,
): boolean {
  // Import restore progress is owned by the bottom-right panel (STUDIO-ASSET-003), including its Revision project_load.
  return (
    entry !== null &&
    !entry.transactionId?.startsWith("editor-import-reload:") &&
    !isOwnedByEditorImportRestore(entry)
  );
}

function isOwnedByEditorImportRestore(entry: ObjectRenderOverlayEntry): boolean {
  if (isEditorImportRestoreTransaction(entry.transactionId)) return true;
  // Unity reports the Revision restore's project_reload render without a transaction; the owner's target Space identifies it.
  const owner = getEditorImportRestoreOwner();
  return (
    owner !== null &&
    !entry.transactionId &&
    (entry.spaceIndex ?? entry.targetSpaceIndex) === owner.targetSpaceIndex
  );
}

interface ObjectRenderOverlayStoreState {
  entries: Record<string, ObjectRenderOverlayEntry>;
  current: ObjectRenderOverlayEntry | null;
  applyUnityState: (detail: UnityObjectRenderStatePayload) => void;
  settleImport: (detail: {
    transactionId: string | null;
    runId?: number | null;
    targetSpaceIndex: number | null;
    currentSpaceIndex: number | null;
    vseIndex: number | null;
  }) => void;
  clear: () => void;
}

export const MIN_OBJECT_RENDER_OVERLAY_MS = 700;
const TERMINAL_TOMBSTONE_TTL_MS = 60_000;

const hideTimers = new Map<string, ReturnType<typeof setTimeout>>();
const terminalKeys = new Map<string, number>();

function pruneTerminalTombstones(now = Date.now()): void {
  terminalKeys.forEach((expiresAt, key) => {
    if (expiresAt <= now) terminalKeys.delete(key);
  });
}

function markTerminalTombstones(keys: readonly string[]): void {
  const expiresAt = Date.now() + TERMINAL_TOMBSTONE_TTL_MS;
  for (const key of keys) {
    terminalKeys.set(key, expiresAt);
  }
}

function wasAlreadyTerminal(key: string): boolean {
  pruneTerminalTombstones();
  return terminalKeys.has(key);
}

function readIndex(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value)
    ? String(value)
    : "unknown";
}

function buildEntryKey(detail: UnityObjectRenderStatePayload): string {
  const transactionKey =
    detail.transactionId ??
    `${detail.operation ?? "object_render"}:${readIndex(
      detail.targetSpaceIndex ?? detail.currentSpaceIndex,
    )}`;
  return [
    transactionKey,
    detail.operation ?? "object_render",
    readIndex(detail.spaceIndex ?? detail.targetSpaceIndex),
    readIndex(detail.vseIndex),
  ].join(":");
}

function isActivePhase(phase: UnityObjectRenderPhase): boolean {
  return phase === "started" || phase === "rendering";
}

function pickCurrent(
  entries: Record<string, ObjectRenderOverlayEntry>,
): ObjectRenderOverlayEntry | null {
  return (
    Object.values(entries).sort((a, b) => b.updatedAt - a.updatedAt)[0] ?? null
  );
}

function findTerminalRemovalKeys(
  exactKey: string,
  detail: UnityObjectRenderStatePayload,
  entries: Record<string, ObjectRenderOverlayEntry>,
): string[] {
  const keys = new Set<string>();
  if (entries[exactKey]) {
    keys.add(exactKey);
  }

  const operation = detail.operation ?? "object_render";
  const targetSpace = readIndex(
    detail.targetSpaceIndex ?? detail.currentSpaceIndex,
  );
  const space = readIndex(detail.spaceIndex ?? detail.targetSpaceIndex);
  const vse = readIndex(detail.vseIndex);

  Object.entries(entries).forEach(([entryKey, entry]) => {
    if (entryKey === exactKey) return;

    const entryOperation = entry.operation ?? "object_render";
    if (entryOperation !== operation) return;

    if (detail.transactionId && entry.transactionId === detail.transactionId) {
      keys.add(entryKey);
      return;
    }

    const entryTargetSpace = readIndex(
      entry.targetSpaceIndex ?? entry.currentSpaceIndex,
    );
    const entrySpace = readIndex(entry.spaceIndex ?? entry.targetSpaceIndex);
    const entryVse = readIndex(entry.vseIndex);
    const sameTargetSpace =
      targetSpace !== "unknown" && entryTargetSpace === targetSpace;
    const sameSpace = space !== "unknown" && entrySpace === space;
    const sameVse = vse !== "unknown" && entryVse === vse;

    if ((sameSpace && sameVse) || (sameTargetSpace && sameVse)) {
      keys.add(entryKey);
    }
  });

  return Array.from(keys);
}

function findImportTerminalRemovalKeys(
  detail: {
    transactionId: string | null;
    runId?: number | null;
    targetSpaceIndex: number | null;
    currentSpaceIndex: number | null;
    vseIndex: number | null;
  },
  entries: Record<string, ObjectRenderOverlayEntry>,
): string[] {
  const targetSpace = readIndex(
    detail.targetSpaceIndex ?? detail.currentSpaceIndex,
  );
  const vse = readIndex(detail.vseIndex);

  const candidates = Object.entries(entries).flatMap(([entryKey, entry]) => {
    if (wasAlreadyTerminal(entryKey)) return [];
    if (detail.runId != null && entry.runId !== detail.runId) return [];
    if (detail.transactionId) {
      // An explicit transaction must never settle another transaction's slot.
      return entry.transactionId === detail.transactionId ? [entryKey] : [];
    }

    const entryTargetSpace = readIndex(
      entry.targetSpaceIndex ?? entry.currentSpaceIndex,
    );
    const entrySpace = readIndex(entry.spaceIndex ?? entry.targetSpaceIndex);
    const entryVse = readIndex(entry.vseIndex);
    const sameTargetSpace =
      targetSpace !== "unknown" &&
      (entryTargetSpace === targetSpace || entrySpace === targetSpace);
    const sameVse = vse !== "unknown" && entryVse === vse;
    return sameTargetSpace && sameVse ? [entryKey] : [];
  });

  // Legacy events have no transaction identity; only one active entry is safe.
  return detail.transactionId || candidates.length === 1 ? candidates : [];
}

function cancelHideTimer(key: string): void {
  const timer = hideTimers.get(key);
  if (!timer) return;
  clearTimeout(timer);
  hideTimers.delete(key);
}

function deleteEntryAfterMinimumVisibleTime(
  key: string,
  entry: ObjectRenderOverlayEntry,
  set: (
    partial:
      | ObjectRenderOverlayStoreState
      | Partial<ObjectRenderOverlayStoreState>
      | ((
          state: ObjectRenderOverlayStoreState,
        ) =>
          | ObjectRenderOverlayStoreState
          | Partial<ObjectRenderOverlayStoreState>),
    replace?: false,
  ) => void,
): boolean {
  const remaining =
    MIN_OBJECT_RENDER_OVERLAY_MS - (Date.now() - entry.startedAt);
  if (remaining <= 0) return false;

  cancelHideTimer(key);
  hideTimers.set(
    key,
    setTimeout(() => {
      hideTimers.delete(key);
      set((state) => {
        if (!state.entries[key]) {
          return state;
        }

        const entries = { ...state.entries };
        delete entries[key];
        return {
          entries,
          current: pickCurrent(entries),
        };
      });
    }, remaining),
  );
  return true;
}

export const useObjectRenderOverlayStore =
  create<ObjectRenderOverlayStoreState>((set) => ({
    entries: {},
    current: null,
    applyUnityState: (detail) => {
      const key = buildEntryKey(detail);
      set((state) => {
        const entries = { ...state.entries };
        const existingEntry = entries[key];
        if (isActivePhase(detail.phase) && detail.status === "in_progress") {
          if (wasAlreadyTerminal(key)) {
            return state;
          }
          cancelHideTimer(key);
          const entry = {
            ...detail,
            key,
            startedAt: existingEntry?.startedAt ?? Date.now(),
            updatedAt: Date.now(),
          };
          entries[key] = entry;
        } else {
          const removalKeys = findTerminalRemovalKeys(key, detail, entries);
          markTerminalTombstones([key, ...removalKeys]);
          if (removalKeys.length === 0) {
            delete entries[key];
          } else {
            removalKeys.forEach((removalKey) => {
              const entry = entries[removalKey];
              if (!entry) return;
              if (deleteEntryAfterMinimumVisibleTime(removalKey, entry, set)) {
                return;
              }
              delete entries[removalKey];
            });
          }
        }
        return {
          entries,
          current: pickCurrent(entries),
        };
      });
    },
    settleImport: (detail) => {
      set((state) => {
        const entries = { ...state.entries };
        const removalKeys = findImportTerminalRemovalKeys(detail, entries);
        markTerminalTombstones(removalKeys);
        if (removalKeys.length === 0) return state;

        removalKeys.forEach((key) => {
          const entry = entries[key];
          if (!entry) return;
          if (deleteEntryAfterMinimumVisibleTime(key, entry, set)) return;
          delete entries[key];
        });

        return {
          entries,
          current: pickCurrent(entries),
        };
      });
    },
    clear: () => {
      hideTimers.forEach((timer) => clearTimeout(timer));
      hideTimers.clear();
      terminalKeys.clear();
      set({ entries: {}, current: null });
    },
  }));
